import React, { useState, useId } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faAngleDoubleDown,
  faSearch,
  faExpand,
  faChevronRight,
  faChevronLeft,
  faQuestionCircle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import "./AyudaTablaRegistros.css";

const Chip = ({ children, hasDot = false }) => (
  <span className={`ayuda-tabla-chip ${hasDot ? "has-dot" : ""}`.trim()}>{children}</span>
);

Chip.propTypes = {
  children: PropTypes.node.isRequired,
  hasDot: PropTypes.bool,
};

const AyudaTablaRegistros = ({ expandible = true, className = "", style = {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const panelId = useId();

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev);
  };

  let counter = 1;

  return (
    <div className={`ayuda-tabla-container ${className}`.trim()} style={style}>
      <button
        type="button"
        className="ayuda-tabla-toggle"
        onClick={toggleVisibility}
        aria-expanded={isVisible}
        aria-controls={panelId}
        title="Ayuda de tabla registros"
      >
        <FontAwesomeIcon
          icon={faQuestionCircle}
          className="ayuda-tabla-icon-question"
          aria-hidden="true"
        />
        <span className="ayuda-tabla-label">Ayuda en tabla de registros</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`ayuda-tabla-chevron ${isVisible ? "is-open" : ""}`}
          aria-hidden="true"
        />
      </button>

      {isVisible && (
        <div id={panelId} className="ayuda-tabla-panel" role="region" aria-label="Guía de uso de la tabla">
          <p className="ayuda-tabla-intro">
            Esta tabla permite buscar, filtrar y ordenar los registros.
          </p>

          <ol className="ayuda-tabla-list">
            {expandible && (
              <>
                <li className="ayuda-tabla-item">
                  <span className="ayuda-tabla-badge" aria-hidden="true">
                    {counter++}
                  </span>
                  <div className="ayuda-tabla-item-content">
                    <span className="TabRegis">Ver los archivos de un registro:</span> clic en{" "}
                    <Chip hasDot>
                      <FontAwesomeIcon icon={faChevronDown} />
                    </Chip>.
                  </div>
                </li>

                <li className="ayuda-tabla-item">
                  <span className="ayuda-tabla-badge" aria-hidden="true">
                    {counter++}
                  </span>
                  <div className="ayuda-tabla-item-content">
                    <span className="TabRegis">Ver los archivos de todos los registros:</span> clic en{" "}
                    <Chip hasDot>
                      <FontAwesomeIcon icon={faAngleDoubleDown} />
                    </Chip>.
                  </div>
                </li>
              </>
            )}

            <li className="ayuda-tabla-item">
              <span className="ayuda-tabla-badge" aria-hidden="true">
                {counter++}
              </span>
              <div className="ayuda-tabla-item-content">
                <span className="TabRegis">Buscar:</span> clic en{" "}
                <Chip>
                  <FontAwesomeIcon icon={faSearch} />
                </Chip>{" "}
                y escribe el texto. Para cerrar la búsqueda, borra el texto y clic en{" "}
                <Chip hasDot>
                  <svg
                    focusable="false"
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    data-testid="SearchOffIcon"
                    style={{ width: 18, height: 18 }}
                  >
                    <path
                      fill="currentColor"
                      d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3 6.08 3 3.28 5.64 3.03 9h2.02C5.3 6.75 7.18 5 9.5 5 11.99 5 14 7.01 14 9.5S11.99 14 9.5 14c-.17 0-.33-.03-.5-.05v2.02c.17.02.33.03.5.03 1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5z"
                    />
                    <path
                      fill="currentColor"
                      d="M6.47 10.82 4 13.29l-2.47-2.47-.71.71L3.29 14 .82 16.47l.71.71L4 14.71l2.47 2.47.71-.71L4.71 14l2.47-2.47z"
                    />
                  </svg>
                </Chip>.
              </div>
            </li>

            <li className="ayuda-tabla-item">
              <span className="ayuda-tabla-badge" aria-hidden="true">
                {counter++}
              </span>
              <div className="ayuda-tabla-item-content">
                <span className="TabRegis">Filtrar:</span> clic en{" "}
                <Chip hasDot>
                  <svg
                    focusable="false"
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    data-testid="FilterListIcon"
                    style={{ width: 18, height: 18 }}
                  >
                    <path
                      fill="currentColor"
                      d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"
                    />
                  </svg>
                </Chip>. Aparecen filtros bajo cada columna (por texto o lista). Para ocultarlos, clic en{" "}
                <Chip hasDot>
                  <svg
                    focusable="false"
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    data-testid="FilterListOffIcon"
                    style={{ width: 18, height: 18 }}
                  >
                    <path
                      fill="currentColor"
                      d="M10.83 8H21V6H8.83l2 2zm5 5H18v-2h-4.17l2 2zM14 16.83V18h-4v-2h3.17l-3-3H6v-2h2.17l-3-3H3V6h.17L1.39 4.22 2.8 2.81l18.38 18.38-1.41 1.41L14 16.83z"
                    />
                  </svg>
                </Chip>.
              </div>
            </li>

            <li className="ayuda-tabla-item">
              <span className="ayuda-tabla-badge" aria-hidden="true">
                {counter++}
              </span>
              <div className="ayuda-tabla-item-content">
                <span className="TabRegis">Elegir columnas:</span> clic en{" "}
                <Chip>
                  <svg
                    focusable="false"
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    data-testid="ViewColumnIcon"
                    style={{ width: 18, height: 18 }}
                  >
                    <path
                      fill="currentColor"
                      d="M14.67 5v14H9.33V5h5.34zm1 14H21V5h-5.33v14zm-7.34 0V5H3v14h5.33z"
                    />
                  </svg>
                </Chip>{" "}
                y marca las que quieras ver.
              </div>
            </li>

            <li className="ayuda-tabla-item">
              <span className="ayuda-tabla-badge" aria-hidden="true">
                {counter++}
              </span>
              <div className="ayuda-tabla-item-content">
                <span className="TabRegis">Pantalla completa:</span> clic en{" "}
                <Chip hasDot>
                  <FontAwesomeIcon icon={faExpand} />
                </Chip>.
              </div>
            </li>

            <li className="ayuda-tabla-item">
              <span className="ayuda-tabla-badge" aria-hidden="true">
                {counter++}
              </span>
              <div className="ayuda-tabla-item-content">
                <span className="TabRegis">Ver más registros:</span> la tabla muestra 10 por página.
                Cambia la cantidad en <span className="TabRegis">Filas por página</span> (parte inferior de la tabla).
                Para cambiar de página usa:
                <ul className="ayuda-tabla-sublist">
                  <li className="ayuda-tabla-subitem">
                    <Chip>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </Chip>
                    <span>Siguiente página</span>
                  </li>
                  <li className="ayuda-tabla-subitem">
                    <Chip>
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </Chip>
                    <span>Página anterior</span>
                  </li>
                  <li className="ayuda-tabla-subitem">
                    <Chip>
                      <svg
                        focusable="false"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        data-testid="FirstPageIcon"
                        style={{ width: 18, height: 18 }}
                      >
                        <path
                          fill="currentColor"
                          d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z"
                        />
                      </svg>
                    </Chip>
                    <span>Primera página</span>
                  </li>
                  <li className="ayuda-tabla-subitem">
                    <Chip>
                      <svg
                        focusable="false"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        data-testid="LastPageIcon"
                        style={{ width: 18, height: 18 }}
                      >
                        <path
                          fill="currentColor"
                          d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z"
                        />
                      </svg>
                    </Chip>
                    <span>Última página</span>
                  </li>
                </ul>
              </div>
            </li>
          </ol>

          <div className="ayuda-tabla-callout" role="note">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="ayuda-tabla-callout-icon"
              aria-hidden="true"
            />
            <div>
              <span className="TabRegis">Nota:</span> si no ves los anexos, usa la
              barra de desplazamiento que está debajo de la tabla.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AyudaTablaRegistros.propTypes = {
  expandible: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default AyudaTablaRegistros;
